<template>
  <div class="container mt-4" v-if="!isLoading">
    <TopNav />
    <div class="row">
      <div class="col-12">
        <img class="mt-3" :srcset="`${logo} 3x`" />
        <p class="mt-4 mb-2">
          {{ $t('SETTINGS.TEXT.CONNECT_YOUR_ACUITY_SCHEDULING_ACCOUNT') }}
        </p>
        <div class="mb-4 d-flex align-items-center">
          <font-awesome-icon
            class="text-primary mr-2"
            :icon="['far', 'info-circle']"
          />
          <a
            href="https://help.heymarvelous.com/en/articles/5195938-acuity-integration-for-private-sessions"
            target="_blank"
          >
            {{
              $t('SETTINGS.LINK.NEED_HELP_WITH_SETTING_UP_ACUITY_SCHEDULING')
            }}
          </a>
        </div>
      </div>
      <div class="col-12 form-group mt-3" v-if="acuity.is_acuity_connected">
        <div class>{{ $t('SETTINGS.TEXT.YOU_ARE_CONNECTED_TO_ACUITY') }}</div>
        <div class="mt-3">
          {{ $t('SETTINGS.TEXT.SELECT_ACUITY_FORM') }}
        </div>
        <v-select
          v-if="acuity"
          class="w-100 mt-2"
          :disabled="inProgress"
          :options="acuity.acuity_intake_forms"
          v-model="acuity.acuity_form_id"
          @input="selectAcuityForm"
          label="name"
          :reduce="
            acuityForm =>
              acuityForm && acuityForm.fields && acuityForm.fields.length
                ? acuityForm.fields[0].id
                : null
          "
        />
        <div
          class="mt-2 d-flex invalid-feedback d-block"
          v-if="!acuity.acuity_form_id"
        >
          {{ $t('SETTINGS.TEXT.MUST_HAVE_FORM_SELECTED') }}
        </div>
        <div
          class="mt-2 invalid-feedback d-block"
          v-if="acuity.acuity_form_id && !isAcuityFormValid"
        >
          {{ $t('SETTINGS.TEXT.FORM_MUST_HAVE_INPUT') }}
        </div>
        <hr class="my-4 w-100" />
        <HMButton
          color="primary"
          @click="disconnect"
          :loading="inProgress"
          :disabled="inProgress"
        >
          {{ $t('SETTINGS.BUTTON.DISCONNECT_ACUITY') }}
        </HMButton>
      </div>
      <div v-else class="col-12 form-group">
        <hr class="my-4 w-100" />
        <HMButton color="primary" @click="connect" :loading="inProgress">
          {{ $t('SETTINGS.BUTTON.CONNECT_TO_ACUITY') }}
        </HMButton>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

import TopNav from '@/web/components/navigation/TopNav.vue';
import { errorToMessage } from '@/core/utils/errors';
import { replaceRedirectURLWithCurrentDomain } from '@/web/utils/url';

export default Vue.extend({
  name: 'AcuityIntegration',
  components: {
    TopNav,
  },
  data() {
    return {
      logo: '/assets/images/integrations/acuity@3x.jpg',
      isLoading: true,
      inProgress: false,
      acuity: null,
    };
  },
  computed: {
    isAcuityFormValid() {
      const form = this.acuity?.acuity_intake_forms.find(
        form => form.fields?.[0]?.id === this.acuity.acuity_form_id
      );

      return form?.fields?.length === 1 && form?.fields[0].type === 'textbox';
    },
  },
  methods: {
    connect() {
      const responseType = 'code';
      const clientId = process.env.VUE_APP_ACUITY_CLIENT_ID;
      const redirectURL = encodeURIComponent(
        replaceRedirectURLWithCurrentDomain(
          process.env.VUE_APP_ACUITY_REDIRECT_URL
        )
      );
      window.location.href = `https://acuityscheduling.com/oauth2/authorize?response_type=${responseType}&scope=api-v1&client_id=${clientId}&redirect_uri=${redirectURL}`;
    },
    disconnect() {
      this.acuity.is_acuity_connected = false;
      this.acuity.acuity_oauth_code = null;
      this.acuity.remove_oauth = true;
      this.acuity.acuity_calendars = null;
      this.acuity.acuity_owner_id = null;
      this.acuity.acuity_form_id = null;
      this.update();
    },
    async update() {
      this.inProgress = true;
      try {
        await this.$store.dispatch('setting/updateAcuityInfo', this.acuity);
        this.acuity = await this.$store.dispatch('setting/getAcuityInfo');
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'SETTINGS.NOTIFICATIONS.ACUITY_INFO_SUCCESSFULLY_UPDATED'
          ),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error.response),
        });
      }
      this.inProgress = false;
    },
    checkForAcuityOAuthCode() {
      const code = this.$route.query.code;
      if (this.$route.query?.error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: `Acuity Scheduling Error: ${this.$route.query?.error}`,
        });
        return;
      }
      if (!code) return;

      const query = { ...this.$route.query };
      delete query.code;
      this.$router.replace({ query });

      this.acuity.is_acuity_connected = true;
      this.acuity.acuity_oauth_code = code;
      this.update();
    },
    selectAcuityForm() {
      if (!this.isAcuityFormValid) return;
      this.update();
    },
  },
  async mounted() {
    try {
      this.acuity = await this.$store.dispatch('setting/getAcuityInfo');
    } catch (error) {
      this.$notify({
        group: 'app',
        type: 'error',
        text: errorToMessage(error.response),
      });
    }
    this.checkForAcuityOAuthCode();
    this.isLoading = false;
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
</style>
